import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  CurrencyFieldExtraProps,
  DimensionSelectorExtraProps,
  FormHandles,
  GenericFormSection,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { QuotesFormTypes } from "models/formTypes";
import { QuotationDetailsFormViewProps } from "models/propTypes/quotationPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useSearchService } from "serviceHooks";
import { GenericForm } from "components/common";
//#endregion

const QuotationDetailsForm: ForwardRefRenderFunction<FormHandles, QuotationDetailsFormViewProps> = (props, ref) => {
  const searchService = useSearchService();
  const { quotation, currency, dimensionAmount } = props;

  const formFields = useMemo((): GenericFormSection<QuotesFormTypes.Details>[] => {
    return [
      {
        label: TranslationKeys.Common.General,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WInfoCircleFill,
        innerFields: {
          customerName: {
            label: TranslationKeys.Invoice.Customer,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
          },
          customerOrgNr: {
            label: TranslationKeys.Common.OrgNo,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
          },
          referencePerson: {
            label: TranslationKeys.Invoice.CustomerReference,
            type: FormEnums.FormFieldType.TEXT,
            maxLength: 128,
          },
          contactPerson: {
            label: TranslationKeys.Invoice.OurReference,
            type: FormEnums.FormFieldType.TEXT,
            maxLength: 128,
          },
        },
      },
      {
        label: TranslationKeys.Common.Amount,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WCalculator,
        innerFields: {
          amountExcludingVat: {
            label: TranslationKeys.Common.AmountExVat,
            type: FormEnums.FormFieldType.CURRENCY,
            extraProps: {
              currencyReferenceFieldName: FormFieldConstants.Invoice.Details.Currency,
            } as CurrencyFieldExtraProps<QuotesFormTypes.Details>,
          },
          vat: {
            label: TranslationKeys.Common.VAT,
            type: FormEnums.FormFieldType.CURRENCY,
            extraProps: {
              currencyReferenceFieldName: FormFieldConstants.Invoice.Details.Currency,
            } as CurrencyFieldExtraProps<QuotesFormTypes.Details>,
          },
          amountIncludingVat: {
            label: TranslationKeys.Common.AmountInclVAT,
            type: FormEnums.FormFieldType.CURRENCY,
            extraProps: {
              currencyReferenceFieldName: FormFieldConstants.Invoice.Details.Currency,
            } as CurrencyFieldExtraProps<QuotesFormTypes.Details>,
          },
          currency: {
            label: TranslationKeys.Common.Currency,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            extraProps: {
              items: searchService.currency,
            } as AutoCompleteExtraProps<QuotesFormTypes.Details>,
          },
        },
      },
      {
        label: TranslationKeys.Common.Dates,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WCalendar,
        innerFields: {
          quotationDate: {
            label: TranslationKeys.Quotation.QuotationDate,
            type: FormEnums.FormFieldType.DATE,
          },
          expireDate: {
            label: TranslationKeys.Quotation.ExpirationDate,
            type: FormEnums.FormFieldType.DATE,
          },
        },
      },
      {
        label: TranslationKeys.Common.Tags,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WHashtag,
        innerFields: {
          dimensions: {
            label: "",
            readonly: false,
            type: FormEnums.FormFieldType.DIMENSIONSELECTOR,
            extraProps: {
              amount: dimensionAmount,
              currency: currency,
              type: DimensionEnums.DimensionCategory.Invoice,
              enableSingleSelectionMode: true,
              allowedTypes: [
                WintSuperkollAPIModel.DimensionCompatibilityType.Project,
                WintSuperkollAPIModel.DimensionCompatibilityType.CostCenter,
              ],
            } as DimensionSelectorExtraProps,
          },
        },
      },
    ];
  }, [quotation, dimensionAmount, currency]);

  return <GenericForm formRef={ref} {...props} fields={formFields} inline isReadOnly />;
};

export default forwardRef(QuotationDetailsForm);
